import { useEffect, useState } from 'react';
import {
    message,
    PageHeader,
    Table,
    Tag,
    Input,
    TablePaginationConfig,
    Select,
    Button,
    Dropdown,
    Menu,
    Tooltip,
} from 'antd';
import { Helmet } from 'react-helmet';

import UsersService from './UsersService';
import styles from './userslistpage.module.css';
import DateDisplay from '../../components/Date';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    EllipsisOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    LockOutlined,
    RocketOutlined,
    SafetyOutlined,
    SmileOutlined,
    StopOutlined,
} from '@ant-design/icons';
import confirm from 'antd/lib/modal/confirm';
import ClientsService from '../clients/ClientsService';

const PAGE_SIZE = 20;

type RouteParams = {
    client: string;
};

const columns = [
    {
        title: 'Nombres',
        dataIndex: 'first_name',
        render: (_val: string, rec: any) =>
            `${rec.first_name} ${rec.last_name}`.trim(),
    },
    { title: 'Correo', dataIndex: 'email', className: styles.stretchCol },
    // { title: 'Celular', dataIndex: 'mobile' },
    {
        title: 'Creado',
        dataIndex: 'created_at',
        className: styles.stretchCol,
        width: 120,
        render: (val: number) => (
            <DateDisplay relative={true}>{val}</DateDisplay>
        ),
    },
    {
        title: 'Estado',
        dataIndex: 'disabled',
        className: `${styles.stretchCol} ${styles.centerCol}`,
        width: 100,
        render: (val: number) =>
            val ? (
                <Tag color="warning">Deshabilitado</Tag>
            ) : (
                <Tag color="success">Activo</Tag>
            ),
    },
    {
        title: 'Rol',
        dataIndex: 'roles',
        className: `${styles.stretchCol} ${styles.centerCol}`,
        width: 40,
        render: (val: string[]) =>
            val.indexOf('superadmin') >= 0 ? (
                <Tooltip title="Super administrador">
                    <RocketOutlined />
                </Tooltip>
            ) : val.indexOf('admin') >= 0 ? (
                <Tooltip title="Administrador">
                    <SafetyOutlined />
                </Tooltip>
            ) : (
                <Tooltip title="Usuario">
                    <SmileOutlined />
                </Tooltip>
            ),
    },
    {
        title: 'Datos sensibles',
        dataIndex: 'roles',
        className: styles.centerCol,
        width: 40,
        render: (val: string[]) =>
            (val.indexOf('superadmin') < 0 && val.indexOf('admin') < 0) ||
            val.indexOf('!sensible_data') >= 0 ? (
                <Tooltip title="Restringidos">
                    <EyeInvisibleOutlined />
                </Tooltip>
            ) : (
                <Tooltip title="Visibles">
                    <EyeOutlined />
                </Tooltip>
            ),
    },
];

const UsersListPage = () => {
    const { client: clientSlug } = useParams() as RouteParams;

    const location = useLocation();
    const navigate = useNavigate();

    const module = ((location.state as any) || {}).module;
    const returnPage = '/admin/evals';

    const [users, setUsers] = useState<any[]>([]);
    const [client, setClient] = useState<any>();
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [disabled, setDisabled] = useState('false');
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: PAGE_SIZE,
        total: 0,
    });
    const [page, setPage] = useState(pagination.current || 1);
    const [lastUpdate, setLastUpdate] = useState<Date>();

    const onQueryChanged = (value: string) => {
        setQuery(value);
        setPagination((pag) => ({ ...pag, current: 1 }));
    };

    const onFilterChanged = (value: string) => {
        setDisabled(value);
        setPagination((pag) => ({ ...pag, current: 1 }));
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setPagination((pag) => ({ ...pag, ...pagination }));
    };

    const onRowClick = (r: any) => {
        navigate(`/admin/clients/${clientSlug}/users/${r.id}/edit`);
    };

    const handleRowAction = async (key: React.Key, user: any) => {
        try {
            switch (key) {
                case 'edit':
                    navigate(
                        `/admin/clients/${clientSlug}/users/${user.id}/edit`,
                        { state: { module } }
                    );
                    break;
                case 'disable':
                    await UsersService.enableDisable(user.id, !user.disabled);
                    message.success(
                        user.disabled
                            ? 'El usuario fue activado'
                            : 'El cliente fue deshabilitado'
                    );
                    setLastUpdate(new Date());
                    break;
                case 'delete':
                    confirm({
                        title: '¿Estás seguro de que quieres eliminar este usuario?',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'Sólo se puede eliminar el usuario si no tiene contenido, de lo contrario se debe deshabilitar. Esta acción no se puede deshacer.',
                        onOk: async () => {
                            try {
                                await UsersService.delete(user.id);
                                setLastUpdate(new Date());
                                message.success('El usuario fue eliminado');
                            } catch (err) {
                                message.error((err as Error).message);
                            }
                        },
                    });
                    break;
            }
        } catch (err) {
            message.error((err as Error).message);
        }
    };

    useEffect(() => {
        setPage(pagination.current || 1);
    }, [pagination]);

    useEffect(() => {
        const queryClient = async () => {
            try {
                const data = await ClientsService.getClientDetails(clientSlug);
                setClient(data);
            } catch (err) {
                message.error((err as Error).message);
            }
        };
        queryClient();
    }, [clientSlug]);

    useEffect(() => {
        const queryUsers = async () => {
            try {
                setLoading(true);
                const data = await UsersService.list(
                    client.id,
                    query,
                    disabled,
                    page,
                    PAGE_SIZE
                );
                setUsers(data.results);
                setPagination((pag) => ({
                    ...pag,
                    total: data.metadata.total,
                }));
            } catch (err) {
                message.error((err as Error).message);
            }

            setLoading(false);
        };
        if (client) queryUsers();
    }, [location, query, page, disabled, lastUpdate, client]);

    const title = client ? `Usuarios de ${client.name}` : 'Usuarios';
    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <PageHeader
                onBack={() => navigate(returnPage)}
                title={title}
                extra={[
                    <Button
                        onClick={() =>
                            navigate(`/admin/clients/${clientSlug}/users/new`, {
                                state: { module: '' },
                            })
                        }
                        type="primary"
                        key="1"
                    >
                        Nuevo usuario
                    </Button>,
                ]}
            ></PageHeader>
            <div className={styles.filters}>
                <Input.Search
                    placeholder="Buscar usuario (ej. 'José Cárdenas')"
                    onSearch={onQueryChanged}
                    style={{ width: 350 }}
                />
                <Select
                    style={{ width: 250 }}
                    defaultValue="false"
                    onChange={onFilterChanged}
                >
                    <Select.Option value="">Mostrar: Todos</Select.Option>
                    <Select.Option value="false">
                        Mostrar: Solo activos
                    </Select.Option>
                    <Select.Option value="true">
                        Mostrar: Solo deshabilitados
                    </Select.Option>
                </Select>
            </div>
            <Table
                className={styles.table}
                rowKey="id"
                size="small"
                rowClassName={(record: any) =>
                    record.disabled ? styles.disabled : ''
                }
                loading={loading}
                dataSource={users}
                columns={[
                    ...columns,
                    {
                        title: '',
                        width: 40,
                        render: (_val: any, record: any) => (
                            <div onClick={(e) => e.stopPropagation()}>
                                <Dropdown
                                    overlay={
                                        <Menu
                                            onClick={({ key }) =>
                                                handleRowAction(key, record)
                                            }
                                        >
                                            <Menu.Item key="edit">
                                                <EditOutlined /> Editar
                                            </Menu.Item>
                                            <Menu.Item key="edit">
                                                <LockOutlined /> Restablecer
                                                contraseña
                                            </Menu.Item>
                                            <Menu.Divider />
                                            <Menu.Item key="disable">
                                                {' '}
                                                {record.disabled ? (
                                                    <>
                                                        <CheckCircleOutlined />{' '}
                                                        Activar
                                                    </>
                                                ) : (
                                                    <>
                                                        <StopOutlined />{' '}
                                                        Deshabilitar
                                                    </>
                                                )}
                                            </Menu.Item>
                                            <Menu.Item key="delete">
                                                <DeleteOutlined /> Eliminar
                                            </Menu.Item>
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <Button type="link">
                                        <EllipsisOutlined />
                                    </Button>
                                </Dropdown>
                            </div>
                        ),
                    },
                ]}
                pagination={{ hideOnSinglePage: true, ...pagination }}
                onChange={handleTableChange}
                onRow={(r) => ({ onClick: () => onRowClick(r) })}
            />
        </>
    );
};

export default UsersListPage;
